import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../Navbar';
import { FaBars } from 'react-icons/fa';
import '../../styles/admin-dashboard.css';

const MOBILE_BREAKPOINT = 991;
const COMPACT_LAPTOP_BREAKPOINT = 1366;

const AdminLayout = ({ children, title, subtitle }) => {
    const { logout, isAdmin, isModerator } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const wasMobileRef = useRef(null);

    // Establish sensible defaults once on mount: off-canvas (closed) on
    // phones/tablets, icon-rail (collapsed) on compact laptops so the sidebar
    // doesn't eat into the working area, full sidebar on larger desktops.
    // Deliberately does not re-run on every resize afterwards so it never
    // fights a manual toggle from the user.
    useEffect(() => {
        const width = window.innerWidth;
        const mobile = width <= MOBILE_BREAKPOINT;
        setIsMobile(mobile);
        wasMobileRef.current = mobile;
        if (!mobile && width <= COMPACT_LAPTOP_BREAKPOINT) {
            setIsSidebarCollapsed(true);
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            if (wasMobileRef.current !== mobile) {
                wasMobileRef.current = mobile;
                setIsMobile(mobile);
                if (mobile) {
                    setIsMobileNavOpen(false);
                }
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSidebar = () => {
        if (isMobile) {
            setIsMobileNavOpen((open) => !open);
        } else {
            setIsSidebarCollapsed((collapsed) => !collapsed);
        }
    };

    const handleLogout = useCallback(() => {
        logout();
        navigate('/');
    }, [logout, navigate]);

    // Close the off-canvas drawer whenever the route changes so picking a
    // nav item on a phone/tablet doesn't leave it covering the new page.
    useEffect(() => {
        setIsMobileNavOpen(false);
    }, [location.pathname]);

    const isActive = (path) => location.pathname === path ? 'active' : '';

    const sidebarCollapsedForDisplay = isMobile ? false : isSidebarCollapsed;

    return (
        <div className="admin-page-wrapper">
            <Navbar />
            <div className={`admin-container ${sidebarCollapsedForDisplay ? 'sidebar-collapsed' : ''}`}>
                {isMobile && isMobileNavOpen && (
                    <div className="admin-sidebar-backdrop show" onClick={() => setIsMobileNavOpen(false)} />
                )}

                {isMobile && !isMobileNavOpen && (
                    <button
                        type="button"
                        className="admin-mobile-toggle-btn"
                        onClick={toggleSidebar}
                        aria-label="Open admin navigation"
                    >
                        <FaBars />
                    </button>
                )}

                {/* Sidebar */}
                <aside
                    className={`admin-sidebar ${sidebarCollapsedForDisplay ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${isMobile && isMobileNavOpen ? 'mobile-open' : ''}`}
                >
                    <div className="admin-brand">
                        <span className="brand-text">{!sidebarCollapsedForDisplay && "Admin Panel"}</span>
                        <button className="sidebar-toggle-btn" onClick={toggleSidebar} aria-label={isMobile ? 'Close admin navigation' : 'Toggle sidebar'}>
                            <i className={`bi bi-${isMobile ? 'x-lg' : (isSidebarCollapsed ? 'layout-sidebar-inset' : 'layout-sidebar-inset-reverse')}`}></i>
                        </button>
                    </div>
                    <nav className="admin-nav">
                        {isAdmin() && (
                            <>
                                <li className="admin-nav-item">
                                    <Link to="/admin" className={`admin-nav-link ${isActive('/admin')}`} title="Dashboard">
                                        <i className="bi bi-speedometer2"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Dashboard</span>}
                                    </Link>
                                </li>
                                <li className="admin-nav-item">
                                    <Link to="/admin/users" className={`admin-nav-link ${isActive('/admin/users')}`} title="Users">
                                        <i className="bi bi-people"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Users</span>}
                                    </Link>
                                </li>
                            </>
                        )}

                        {(isAdmin() || isModerator()) && (
                            <li className="admin-nav-item">
                                <Link to="/moderationPage" className={`admin-nav-link ${isActive('/moderationPage')}`} title="Moderation">
                                    <i className="bi bi-shield-check"></i>
                                    {!sidebarCollapsedForDisplay && <span className="nav-label">Moderation</span>}
                                </Link>
                            </li>
                        )}

                        {isAdmin() && (
                            <>
                                <li className="admin-nav-item">
                                    <Link to="/admin/feedback" className={`admin-nav-link ${isActive('/admin/feedback')}`} title="Feedback">
                                        <i className="bi bi-chat-left-text"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Feedback</span>}
                                    </Link>
                                </li>
                                <li className="admin-nav-item">
                                    <Link to="/admin/manage-entries" className={`admin-nav-link ${isActive('/admin/manage-entries')}`} title="Manage Entries">
                                        <i className="bi bi-list-ul"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Manage Entries</span>}
                                    </Link>
                                </li>
                                <li className="admin-nav-item">
                                    <Link to="/admin/datasets" className={`admin-nav-link ${isActive('/admin/datasets')}`} title="Datasets">
                                        <i className="bi bi-database"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Datasets</span>}
                                    </Link>
                                </li>
                                <li className="admin-nav-item">
                                    <Link to="/admin/metadata" className={`admin-nav-link ${isActive('/admin/metadata')}`} title="Metadata">
                                        <i className="bi bi-tags"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Metadata</span>}
                                    </Link>
                                </li>
                                <li className="admin-nav-item">
                                    <Link to="/admin/cas-search" className={`admin-nav-link ${isActive('/admin/cas-search')}`} title="CAS Search">
                                        <i className="bi bi-search"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">CAS Search</span>}
                                    </Link>
                                </li>
                                <li className="admin-nav-item">
                                    <Link to="/admin/email" className={`admin-nav-link ${location.pathname.startsWith('/admin/email') ? 'active' : ''}`} title="Email Center">
                                        <i className="bi bi-envelope"></i>
                                        {!sidebarCollapsedForDisplay && <span className="nav-label">Email Center</span>}
                                    </Link>
                                </li>

                            </>
                        )}
                    </nav>
                    <div className="admin-logout">
                        <button onClick={handleLogout} className="btn-admin-logout" title="Logout">
                            <i className="bi bi-box-arrow-right"></i>
                            {!sidebarCollapsedForDisplay && <span className="nav-label" style={{ marginLeft: '0.75rem' }}>Logout</span>}
                        </button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="admin-main">
                    <header className="admin-header">
                        <h1>{title || 'Admin Panel'}</h1>
                        {subtitle && <p>{subtitle}</p>}
                    </header>

                    {children}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
